import type { Allocation } from './planning.js';
import type { Project } from './projects.js';
import type { WorkPackage } from './work-packages.js';

/**
 * Konflikt bei gleichzeitiger Planung (AC-409): der Vertrag zwischen Server
 * und Oberfläche, wenn ein Schreibvorgang mit einer überholten Version
 * eintrifft.
 *
 * Zod-frei und als Unterpfad `@asas/domain/konflikt` für den Browser
 * freigegeben (Regel 27): die Oberfläche braucht `istOptimisticLockConflict`
 * zur Laufzeit, um die Nutzdaten aus der Fehlerantwort zu lesen.
 *
 * Zwei Werte, eine Entscheidung beim Menschen: `serverState` ist der Stand,
 * der gerade gespeichert ist, `attemptedState` der abgewiesene Versuch. Beide
 * enthalten nur die Felder aus `KONFLIKT_FELDER` — fachlich relevant und für
 * den Aufrufer ohnehin lesbar, keine Rohzeile. Ein Feld, das dort fehlt,
 * verlässt den Server nicht, auch wenn ein Dienst es versehentlich mitgibt.
 *
 * Entscheidungen: docs/stories/AC-409/ENTSCHEIDUNGEN.md.
 */

/** Die drei Entitäten mit optimistischer Sperre (AK 3; Tabellennamen). */
export const VERSIONIERTE_ENTITAETEN = ['projects', 'work_packages', 'allocation_weeks'] as const;
export type VersionierteEntitaet = (typeof VERSIONIERTE_ENTITAETEN)[number];

/** Alles, was mit einer Version geschrieben wird (`version INT NOT NULL DEFAULT 0`). */
export interface Versioned {
  readonly version: number;
}

/** Was je Entität in einem Konflikt gezeigt wird. */
export const KONFLIKT_FELDER = {
  projects: [
    'number',
    'name',
    'clientName',
    'projectManagerId',
    'companyId',
    'status',
    'fee',
    'beauftragteLeistungsphasen',
  ],
  work_packages: ['planHours'],
  allocation_weeks: [
    'planHours',
    'freigabeStatus',
    'belegtDurch',
    'projectId',
    'workPackageId',
    'woche',
  ],
} as const satisfies Record<VersionierteEntitaet, readonly string[]>;

export interface KonfliktStaende {
  projects: Pick<Project, (typeof KONFLIKT_FELDER.projects)[number]>;
  work_packages: Pick<WorkPackage, (typeof KONFLIKT_FELDER.work_packages)[number]>;
  allocation_weeks: Pick<Allocation, (typeof KONFLIKT_FELDER.allocation_weeks)[number]>;
}

export const OPTIMISTIC_LOCK_CONFLICT = 'OPTIMISTIC_LOCK_CONFLICT';

/**
 * Die Nutzdaten eines Konflikts, wie sie in `error.data.konflikt` beim Client
 * ankommen. `attemptedState` ist teilweise: ein Statuswechsel versucht nur
 * den Status, eine Stundenänderung nur die Stunden.
 */
export interface OptimisticLockConflict<E extends VersionierteEntitaet = VersionierteEntitaet> {
  readonly kind: typeof OPTIMISTIC_LOCK_CONFLICT;
  readonly entity: E;
  readonly id: string;
  readonly expectedVersion: number;
  readonly actualVersion: number;
  readonly serverState: KonfliktStaende[E];
  readonly attemptedState: Partial<KonfliktStaende[E]>;
}

/**
 * Ein Versuch, wie ihn eine Eingabe liefert: ein ausgelassenes Feld
 * (`undefined`) heißt „unverändert“ und erscheint nicht im Konflikt.
 */
export type KonfliktVersuch<E extends VersionierteEntitaet> = {
  readonly [K in keyof KonfliktStaende[E]]?: KonfliktStaende[E][K] | undefined;
};

/**
 * Kopiert nur die freigegebenen Felder; unbekannte bleiben draußen, ebenso
 * ausgelassene (`undefined`).
 */
function nurFreigegeben(entity: VersionierteEntitaet, quelle: object): Record<string, unknown> {
  const felder: readonly string[] = KONFLIKT_FELDER[entity];
  const roh = quelle as Record<string, unknown>;

  return Object.fromEntries(
    felder
      .filter((feld) => Object.hasOwn(roh, feld) && roh[feld] !== undefined)
      .map((feld) => [feld, roh[feld]]),
  );
}

/**
 * Baut die Nutzdaten aus dem gespeicherten Stand und dem Versuch. Beide
 * Objekte dürfen mehr Felder tragen (eine ganze Belegung, die ganze Eingabe);
 * übernommen wird nur, was `KONFLIKT_FELDER` nennt.
 */
export function optimisticLockConflict<E extends VersionierteEntitaet>(angaben: {
  readonly entity: E;
  readonly id: string;
  readonly expectedVersion: number;
  readonly server: KonfliktStaende[E] & Versioned;
  readonly attempted: KonfliktVersuch<E>;
}): OptimisticLockConflict<E> {
  return {
    kind: OPTIMISTIC_LOCK_CONFLICT,
    entity: angaben.entity,
    id: angaben.id,
    expectedVersion: angaben.expectedVersion,
    actualVersion: angaben.server.version,
    serverState: nurFreigegeben(angaben.entity, angaben.server) as KonfliktStaende[E],
    attemptedState: nurFreigegeben(angaben.entity, angaben.attempted) as Partial<
      KonfliktStaende[E]
    >,
  };
}

/**
 * Bereinigt Nutzdaten erneut auf die Whitelist. Die Fehlerform der API
 * (`buildErrorShape`) ruft das vor der Auslieferung auf — dieselbe Linie wie
 * dort: was nicht ausdrücklich erlaubt ist, bleibt draußen.
 */
export function bereinigeKonflikt(konflikt: OptimisticLockConflict): OptimisticLockConflict {
  return {
    kind: OPTIMISTIC_LOCK_CONFLICT,
    entity: konflikt.entity,
    id: konflikt.id,
    expectedVersion: konflikt.expectedVersion,
    actualVersion: konflikt.actualVersion,
    serverState: nurFreigegeben(
      konflikt.entity,
      konflikt.serverState,
    ) as KonfliktStaende[typeof konflikt.entity],
    attemptedState: nurFreigegeben(konflikt.entity, konflikt.attemptedState),
  };
}

/**
 * Der Schreibvorgang traf eine überholte Version; nichts wurde geschrieben.
 * Die API bildet ihn auf `CONFLICT` ab und gibt `konflikt` in
 * `error.data.konflikt` heraus. Die Fachfehler der Dienste
 * (`ProjectVersionConflictError` usw.) leiten davon ab.
 */
export class OptimisticLockConflictError<
  E extends VersionierteEntitaet = VersionierteEntitaet,
> extends Error {
  constructor(
    readonly konflikt: OptimisticLockConflict<E>,
    message = 'Der Datensatz wurde inzwischen geändert (AC-409).',
  ) {
    super(message);
    this.name = 'OptimisticLockConflictError';
  }
}

function istVersion(wert: unknown): wert is number {
  return typeof wert === 'number' && Number.isInteger(wert) && wert >= 0;
}

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert);
}

/**
 * Prüft Nutzdaten aus dem Netz, ohne Zod (Browser). Geprüft wird die Hülle;
 * die Werte in `serverState`/`attemptedState` zeigt die Oberfläche an und
 * schickt sie nicht ungeprüft zurück.
 */
export function istOptimisticLockConflict(wert: unknown): wert is OptimisticLockConflict {
  if (!istObjekt(wert)) return false;

  return (
    wert.kind === OPTIMISTIC_LOCK_CONFLICT &&
    typeof wert.entity === 'string' &&
    (VERSIONIERTE_ENTITAETEN as readonly string[]).includes(wert.entity) &&
    typeof wert.id === 'string' &&
    istVersion(wert.expectedVersion) &&
    istVersion(wert.actualVersion) &&
    istObjekt(wert.serverState) &&
    istObjekt(wert.attemptedState)
  );
}

const DEZIMAL = /^(\d+)(?:\.(\d+))?$/;

/**
 * Nur diese Felder sind Dezimalwerte (`NUMERIC` als Text). Textfelder wie die
 * Projektnummer werden wörtlich verglichen: `"24.1"` und `"24.10"` sind dort
 * zwei verschiedene Nummern.
 */
const DEZIMAL_FELDER: ReadonlySet<string> = new Set(['planHours', 'fee']);

/**
 * Eine Dezimalzahl als Text in Normalform, ohne `Number` (Regel 10): `"16"`,
 * `"16.0"` und `"016.00"` werden zu `"16"`. Alles andere bleibt, wie es ist.
 */
function kanonisch(wert: unknown): unknown {
  if (typeof wert !== 'string') return wert;
  const treffer = DEZIMAL.exec(wert);
  if (treffer === null) return wert;
  const ganz = (treffer[1] ?? '').replace(/^0+(?=\d)/, '');
  const bruch = (treffer[2] ?? '').replace(/0+$/, '');

  return bruch.length > 0 ? `${ganz}.${bruch}` : ganz;
}

/**
 * Beschreibt der gespeicherte Stand bereits den Versuch? Dann hat ein früherer
 * Versuch gewirkt (verlorene Antwort) oder jemand hat dasselbe eingetragen —
 * in beiden Fällen ist nichts zu entscheiden. Nur Dezimalfelder (Stunden,
 * Honorar; `DEZIMAL_FELDER`) vergleicht `kanonisch` als Text: die Eingabe `"16"` beschreibt den
 * gespeicherten Stand `"16.00"`.
 */
export function konfliktBereitsErfuellt(konflikt: OptimisticLockConflict): boolean {
  const server = konflikt.serverState as Record<string, unknown>;
  const versuch = Object.entries(konflikt.attemptedState as Record<string, unknown>);

  return (
    versuch.length > 0 &&
    versuch.every(([feld, wert]) =>
      DEZIMAL_FELDER.has(feld)
        ? JSON.stringify(kanonisch(server[feld])) === JSON.stringify(kanonisch(wert))
        : JSON.stringify(server[feld]) === JSON.stringify(wert),
    )
  );
}
